/**
 * App mode — what this storefront link is allowed to do.
 *
 * A menu link is authored in the dashboard with a `type`, surfaced on the
 * public config payload (`OnlineOrderingConfig.type`). v1 read it and gated
 * the whole app on it; v2 fetched it and ignored it, so a digital-menu-only
 * link rendered the full ordering app (sign-in, cart, checkout, payment).
 * This module is the single place that decision is made.
 *
 * Three seams, because three callers need different things:
 *   - `resolveAppMode()` is pure, so the decision table is testable with no
 *     React and no mocks.
 *   - a module-level cell (`setAppMode` / `isMenuOnly`) so `apiFetch` — which
 *     is not a component and cannot read context — can enforce the mode at the
 *     network layer. This mirrors how `api/http.ts` already handles the tenant
 *     API key.
 *   - `useAppMode()` (in `hooks/useAppMode.ts`) for components.
 */
import type { OnlineOrderingConfig } from "@/api/online-ordering-service/model";

export type AppMode =
  /** Config has not resolved yet. Neither ordering nor menu-only. */
  | "unresolved"
  /** Browse + shortlist only. No accounts, no cart, no checkout. */
  | "menuOnly"
  /** Table-scoped ordering. */
  | "dineIn"
  /** Full online ordering. */
  | "ordering";

/** The one `type` value that disables ordering. Compared exactly. */
const MENU_ONLY_TYPE = "digitalMenu";
const DINE_IN_TYPE = "dineIn";

/**
 * Decide the mode from the resolved config and whether a table context is
 * active.
 *
 * Fail-open by design: only the exact string `"digitalMenu"` disables
 * ordering. An unknown, renamed, empty or missing `type` behaves as ordering,
 * because the failure mode of guessing wrong in that direction (one merchant
 * shows an order button they didn't want) is far cheaper than the alternative
 * (a backend rename silently stops the whole platform taking orders).
 *
 * Note `menuOnly` is checked *before* `hasTable`. In v1 appending
 * `?tableId=anything` to a digital-menu URL promoted it into dine-in ordering;
 * here the merchant's configured type wins. A merchant who wants QR table
 * ordering sets the link type in the dashboard.
 */
export function resolveAppMode(
  config: OnlineOrderingConfig | null | undefined,
  hasTable: boolean,
): AppMode {
  if (!config) return "unresolved";
  const type = (config as { type?: unknown }).type;
  if (type === MENU_ONLY_TYPE) return "menuOnly";
  if (hasTable) return "dineIn";
  if (type === DINE_IN_TYPE) return "dineIn";
  return "ordering";
}

let currentMode: AppMode = "unresolved";

/**
 * Publish the resolved mode to non-React consumers. Called from
 * `store/branch.tsx` beside `setApiKey()`.
 */
export function setAppMode(mode: AppMode): void {
  currentMode = mode;
}

export function currentAppMode(): AppMode {
  return currentMode;
}

/**
 * True only when ordering is affirmatively disabled. `unresolved` is false:
 * the network guard must not reject calls in flight during bootstrap, and the
 * UI suppresses mode-dependent chrome by checking for `"ordering"` explicitly
 * rather than by negating this.
 */
export function isMenuOnly(): boolean {
  return currentMode === "menuOnly";
}

/** Test-only: reset the module cell between cases. */
export function resetAppMode(): void {
  currentMode = "unresolved";
}
